import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestService } from '../../../services/rest/rest.service';
import { RestOptions } from '../../../services/rest/rest-request';

import {
  Account,
  AccountDetailsResponse,
  AccountGroup,
  AccountGroupResponse,
  AccountHelper,
  ContributionResponse,
  RrifPaymentResponse
} from '../../../domain/public-api';
import { AccountsContainer } from './accounts-container';
import { AccountsDescriptionService, AccountsDescriptionType } from './accounts-description.service';
import { AccountsSortingService } from './accounts-sorting.service';

@Injectable({
  providedIn: 'root'
})
export class AccountsRestService {

  constructor(private restService: RestService,
              private accountsDescriptionService: AccountsDescriptionService,
              private accountsSortingService: AccountsSortingService) {
  }

  getAccounts<T extends Account>(filterName: string | Array<string> = undefined): Observable<AccountsContainer<T>> {
    const parameters: RestOptions = {
      params: {
        filter: filterName
      }
    };
    return this.restService.getResource('account_v2.all').get<Array<T>>(parameters)
      .pipe(
        map( (response: Array<T>): AccountsContainer<T> => {
          const accounts: Array<T> = response;
          this.accountsDescriptionService.setDescriptions(accounts);
          return new AccountsContainer(this.accountsSortingService.sort(accounts));
        })
      );
  }

  getAccountGroups(filterName: string | Array<string> = undefined,
                   descriptionType: AccountsDescriptionType = AccountsDescriptionType.NO_NICKNAME): Observable<Array<AccountGroup>> {
    const parameters: RestOptions = {
      params: {
        filter: filterName
      }
    };
    return this.restService.getResource('account_v2.accountGroups').get<Array<AccountGroupResponse>>(parameters)
      .pipe(
        map((response: Array<AccountGroupResponse>): Array<AccountGroup> => {
          const groups: Array<AccountGroup> = response;
          this.accountsDescriptionService.setAccountGroupDescriptions(groups, descriptionType);
          this.accountsSortingService.sortAccountGroups(groups);
          return groups;
        })
      );
  }

  getAccountDetails(accountId: string, filters: string | Array<string>): Observable<AccountDetailsResponse> {
    const parameters = {
      pathVariables: { accountId: decodeURIComponent(accountId) },
      params: {
        filter: filters
      }
    };

    return this.restService.getResource('account_v2.details').get<AccountDetailsResponse>(parameters);
  }

  getContributionInformation(accountId: string): Observable<ContributionResponse> {
    const parameters = {
      pathVariables: {
        accountId: decodeURIComponent(accountId)
      }
    };
    return this.restService.getResource('account_v2.contributions').get<ContributionResponse>(parameters);
  }

  getPaymentInformation(account: Account): Observable<RrifPaymentResponse> {
    if (!AccountHelper.isPaymentInformationAvailable(account)) {
      return of(undefined);
    }
    const parameters = {
      params: {
        accountId: decodeURIComponent(account.id)
      }
    };
    return this.restService.getResource('account_v2.rrifPayments').get<RrifPaymentResponse>(parameters);
  }
}
